/**
 * Wire schemas and pure decision functions for the CMIA disclosure/authorization workflow
 * (`BACKLOG.md` C14, `docs/decisions/cmia-disclosure-scope.md`). That doc is the spec this module
 * implements — read it before changing anything here, especially the "Open legal questions" section,
 * since two of the vocabularies below (`DISCLOSURE_NO_AUTHORIZATION_PURPOSES` and the one-year
 * expiration cap) are this pass's best-effort reading of Civil Code §56.10/§56.11, not a legal
 * sign-off.
 *
 * Split the way `careTeam.ts`/`confidentiality.ts` are: the *decision* functions here
 * (`resolveDisclosureAuthorizationStatus`, `computeDisclosureAuthorizationExpiresAt`) are pure and
 * covered by `disclosures.test.ts` with no database — `claude-kit/rules/invariants.md`'s "Business
 * rules live in pure, tested modules — never inline in a component" — the DB-touching half lives in
 * `server/src/clinical/disclosuresService.ts`.
 */

import { z } from 'zod';

import { CHART_EXPORT_RECORD_TYPES, chartExportRecordTypeSchema } from './clinicalRequests.ts';

// --- Closed vocabularies ----------------------------------------------------------------------

/**
 * §56.10(c)'s own closed, enumerated no-authorization-required disclosure purposes, as read by
 * `cmia-disclosure-scope.md`'s "What CMIA actually requires" section — narrower than HIPAA's TPO
 * mental model on purpose (CMIA's list is closed and specific, not the open "health oversight
 * activities" shape §164.512 allows). **Deliberately excludes `marketing`** per §56.10(d)'s flat
 * restriction — the decision doc's own text: "the safe design decision made here is that the closed
 * disclosure-purpose enum simply never offers `marketing` as a value, so the question of 'under what
 * authorization could marketing be permitted' never becomes reachable through this system regardless
 * of how the legal question eventually resolves." This is this pass's own reading of the statute, not
 * a counsel-approved list — see the decision doc's "Open legal questions" §2.
 */
export const DISCLOSURE_NO_AUTHORIZATION_PURPOSES = [
  'treatment_coordination',
  'payment',
  'quality_review',
  'public_health_reporting',
  'research',
  'compelled_legal_process',
] as const;
export type DisclosureNoAuthorizationPurpose =
  (typeof DISCLOSURE_NO_AUTHORIZATION_PURPOSES)[number];
export const disclosureNoAuthorizationPurposeSchema = z.enum(DISCLOSURE_NO_AUTHORIZATION_PURPOSES);

/** Whether a disclosure event rests on a signed patient authorization or a statutory
 * no-authorization-required basis. */
export const DISCLOSURE_BASIS_TYPES = ['authorization', 'no_authorization_required'] as const;
export type DisclosureBasisType = (typeof DISCLOSURE_BASIS_TYPES)[number];
export const disclosureBasisTypeSchema = z.enum(DISCLOSURE_BASIS_TYPES);

/** How the disclosed content actually left the practice. Open-but-checked text, matching
 * `appointments.status`'s convention. */
export const DISCLOSURE_METHODS = ['fax', 'mail', 'secure_download', 'in_person'] as const;
export type DisclosureMethod = (typeof DISCLOSURE_METHODS)[number];
export const disclosureMethodSchema = z.enum(DISCLOSURE_METHODS);

/**
 * `patient_disclosure_authorizations.status`'s closed value set. `active`/`revoked`/`superseded` are
 * the only values a write ever stores — `expired` is never written, only *computed* at read time
 * against `expiresAt` (`resolveDisclosureAuthorizationStatus` below), the same "resolved at read
 * time rather than from a stored flag" reasoning `legalDocuments.ts`'s `selectCurrentDocument`
 * already establishes for "current". It is still part of the closed set the database CHECK
 * constraint accepts, since a future backfill or read-repair path may legitimately want to persist
 * it — narrower than that would make the column's own CHECK constraint disagree with this shared
 * union for no real benefit.
 */
export const DISCLOSURE_AUTHORIZATION_STATUSES = [
  'active',
  'revoked',
  'expired',
  'superseded',
] as const;
export type DisclosureAuthorizationStatus = (typeof DISCLOSURE_AUTHORIZATION_STATUSES)[number];
export const disclosureAuthorizationStatusSchema = z.enum(DISCLOSURE_AUTHORIZATION_STATUSES);

// --- Pure decisions ----------------------------------------------------------------------------

/** §56.11's expiration cap: one year from signing, unless the authorization is for a clinical trial
 * or research study (`researchException`). */
export const DISCLOSURE_AUTHORIZATION_MAX_DURATION_MS = 365 * 24 * 60 * 60 * 1000;

export interface ComputeDisclosureAuthorizationExpiresAtInput {
  /** ISO instant. */
  readonly signedAt: string;
  /** The practice's chosen duration, as an ISO instant — `undefined` means "use the maximum allowed
   * duration", never "no expiration": §56.11 requires a stated expiration unconditionally, so there
   * is no code path that produces one at all. */
  readonly requestedExpiresAt: string | undefined;
  readonly researchException: boolean;
}

/**
 * §56.11's expiration-cap requirement, enforced structurally rather than trusted to every caller:
 * computed here, once, rather than validated-and-rejected at the route boundary, so a caller cannot
 * accidentally (or deliberately) produce an authorization that outlives the statutory cap by
 * requesting a too-far date. A `researchException` authorization is **not capped** — §56.11's own
 * carve-out — but still requires a real, caller-stated `requestedExpiresAt`: the exception widens who
 * may set the date, it does not invent one. This codebase has no independent way to verify a
 * `researchException` claim is actually true; it is trusted the same way every other
 * capability-gated, caller-asserted clinical fact in this system is (`docs/decisions/cmia-disclosure-
 * scope.md`'s own "Signer eligibility" note names this as a real, unverified gap, not solved here).
 */
export function computeDisclosureAuthorizationExpiresAt(
  input: ComputeDisclosureAuthorizationExpiresAtInput,
): string {
  const signedAtMs = new Date(input.signedAt).getTime();
  const capMs = signedAtMs + DISCLOSURE_AUTHORIZATION_MAX_DURATION_MS;

  if (input.researchException) {
    const requestedMs = input.requestedExpiresAt
      ? new Date(input.requestedExpiresAt).getTime()
      : capMs;
    return new Date(requestedMs).toISOString();
  }

  const requestedMs = input.requestedExpiresAt
    ? new Date(input.requestedExpiresAt).getTime()
    : capMs;
  return new Date(Math.min(requestedMs, capMs)).toISOString();
}

export interface DisclosureAuthorizationStatusRow {
  /** The stored column's value — `active` | `revoked` | `superseded` in ordinary operation (see
   * `DISCLOSURE_AUTHORIZATION_STATUSES`'s own comment on why `expired` is never actually written). */
  readonly status: string;
  readonly expiresAt: string;
}

/**
 * The *effective* status of an authorization as of `asOf` — `legalDocuments.ts`'s
 * `selectCurrentDocument` precedent applied to this table: an explicit terminal state
 * (`revoked`/`superseded`) always wins over the date, since those are deliberate staff actions that
 * do not un-happen when a clock ticks backward; short of that, the row is `active` until
 * `expiresAt`, then `expired`. Every route that gates a disclosure on "is the referenced
 * authorization active" calls this rather than reading the stored `status` column directly — the
 * chokepoint discipline `claude-kit/rules/invariants.md`'s "Centralize the actual authorization
 * decision, not just the default" asks for, applied to this table's own read-time status question.
 */
export function resolveDisclosureAuthorizationStatus(
  row: DisclosureAuthorizationStatusRow,
  asOf: Date,
): DisclosureAuthorizationStatus {
  if (row.status === 'revoked' || row.status === 'superseded') return row.status;
  if (new Date(row.expiresAt).getTime() <= asOf.getTime()) return 'expired';
  return 'active';
}

// --- Request/response schemas --------------------------------------------------------------------

/** A PDF only, matching `calendarSync.ts`'s `uploadProviderBaaRequestSchema` — a CMIA authorization
 * is a signed legal instrument, not an image, the same "restricted to `application/pdf`" reasoning
 * `provider_baa_records.storageRef` already establishes for the identical shape. JSON+base64, not
 * `multipart/form-data` — this codebase's one established upload convention for a body that has to
 * carry bytes (`patientPhotoRequests.ts`'s own header states why). */
const MAX_DISCLOSURE_AUTHORIZATION_DOCUMENT_FILENAME_LENGTH = 255;
/** ~10 MB of decoded PDF bytes, base64-inflated — the same generous ceiling
 * `MAX_BAA_DOCUMENT_BASE64_LENGTH` uses for the identical "a signed PDF, not a photo" shape. */
export const MAX_DISCLOSURE_AUTHORIZATION_DOCUMENT_BASE64_LENGTH = 14_000_000;

const MAX_DISCLOSURE_TEXT_FIELD_LENGTH = 4000;

export const createDisclosureAuthorizationRequestSchema = z.strictObject({
  recipientName: z.string().trim().min(1).max(MAX_DISCLOSURE_TEXT_FIELD_LENGTH),
  recipientDescription: z.string().trim().min(1).max(MAX_DISCLOSURE_TEXT_FIELD_LENGTH).optional(),
  informationScope: z.string().trim().min(1).max(MAX_DISCLOSURE_TEXT_FIELD_LENGTH),
  /** The purpose exactly as stated on the signed authorization — distinct from
   * `recordDisclosureRequestSchema`'s closed `noAuthorizationPurpose` enum, which a free-text field
   * cannot substitute for (`docs/decisions/cmia-disclosure-scope.md`'s own reasoning). */
  purposeText: z.string().trim().min(1).max(MAX_DISCLOSURE_TEXT_FIELD_LENGTH),
  signedAt: z.iso.datetime({ offset: true }),
  /** Omitted means "the maximum §56.11 allows" — see `computeDisclosureAuthorizationExpiresAt`.
   * Never a caller-supplied *final* value: the server always recomputes it against the cap, so a
   * caller cannot request an out-of-bounds date by construction. */
  requestedExpiresAt: z.iso.datetime({ offset: true }).optional(),
  researchException: z.boolean().optional(),
  /** Set when the patient signed for themselves — the route validates this equals the path's own
   * `:patientId` before accepting it (this authorization's subject is the only patient who could
   * plausibly be its own signer), rather than trusting an arbitrary caller-supplied patient id. */
  signedByPatient: z.boolean().optional(),
  /**
   * A representative/guardian's identity, when known — `docs/decisions/cmia-disclosure-scope.md`'s
   * own "What this pass does not cover" section names the absence of a real legal-representative
   * model as a deliberate, unbuilt gap: this codebase has no table a "representative" id could
   * meaningfully reference, so this field is stored (`patient_disclosure_authorizations
   * .signed_by_representative_user_id`) but never validated against anything, the same "honest
   * stand-in, not a claim that it is one" posture `kiosk/service.ts`'s own header uses for an
   * unbuilt rules engine. `signerRelationship` below is the field this workflow actually depends on.
   */
  signedByRepresentativeUserId: z.uuid().optional(),
  /** Free text — "parent", "conservator", "self", "spouse (insurance application)". Required
   * unconditionally: §56.11 requires *someone's* relationship to the patient to be recorded, and a
   * real legal-representative model does not exist yet to derive it from structurally. */
  signerRelationship: z.string().trim().min(1).max(200),
  documentFilename: z
    .string()
    .trim()
    .min(1)
    .max(MAX_DISCLOSURE_AUTHORIZATION_DOCUMENT_FILENAME_LENGTH),
  documentContentBase64: z.base64().min(1).max(MAX_DISCLOSURE_AUTHORIZATION_DOCUMENT_BASE64_LENGTH),
});
export type CreateDisclosureAuthorizationRequest = z.infer<
  typeof createDisclosureAuthorizationRequestSchema
>;

export const disclosureAuthorizationResponseSchema = z.strictObject({
  id: z.uuid(),
  patientId: z.uuid(),
  recipientName: z.string(),
  recipientDescription: z.string().nullable(),
  informationScope: z.string(),
  purposeText: z.string(),
  signedAt: z.string(),
  expiresAt: z.string(),
  researchException: z.boolean(),
  signedByPatientId: z.uuid().nullable(),
  signedByRepresentativeUserId: z.uuid().nullable(),
  signerRelationship: z.string(),
  /** The effective status as of the response's own generation time — see
   * `resolveDisclosureAuthorizationStatus`. Never the raw stored column value on its own. */
  status: disclosureAuthorizationStatusSchema,
  revokedAt: z.string().nullable(),
  revokedByUserId: z.uuid().nullable(),
  createdByUserId: z.uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DisclosureAuthorizationResponse = z.infer<typeof disclosureAuthorizationResponseSchema>;

export const createDisclosureAuthorizationResponseSchema = z.strictObject({
  authorization: disclosureAuthorizationResponseSchema,
});
export type CreateDisclosureAuthorizationResponse = z.infer<
  typeof createDisclosureAuthorizationResponseSchema
>;

export const revokeDisclosureAuthorizationResponseSchema = z.strictObject({
  authorization: disclosureAuthorizationResponseSchema,
});
export type RevokeDisclosureAuthorizationResponse = z.infer<
  typeof revokeDisclosureAuthorizationResponseSchema
>;

/**
 * `recordDisclosureRequestSchema`'s own refine enforces the pairing `docs/decisions/cmia-disclosure-
 * scope.md` specifies: `authorizationId` present iff `basisType = 'authorization'`,
 * `noAuthorizationPurpose` present iff `basisType = 'no_authorization_required'` — the same
 * exactly-one-of shape `patient_disclosures`'s own CHECK constraint enforces at the database layer,
 * checked here too so a malformed request is rejected before it reaches that constraint.
 */
export const recordDisclosureRequestSchema = z
  .strictObject({
    basisType: disclosureBasisTypeSchema,
    authorizationId: z.uuid().optional(),
    noAuthorizationPurpose: disclosureNoAuthorizationPurposeSchema.optional(),
    /** Denormalized at disclosure time even when `authorizationId` is set — see
     * `patient_disclosures.recipientName`'s own schema-file comment for why this is never read back
     * from the authorization's own `recipientName` instead. */
    recipientName: z.string().trim().min(1).max(MAX_DISCLOSURE_TEXT_FIELD_LENGTH),
    recordTypesDisclosed: z
      .array(chartExportRecordTypeSchema)
      .min(1)
      .max(CHART_EXPORT_RECORD_TYPES.length),
    method: disclosureMethodSchema,
    /** Omitted means "now" — resolved server-side, matching `careTeamRequests.ts`'s own
     * `startedAt` reasoning: a caller-supplied *past* instant is a legitimate input (recording a
     * disclosure that already happened before anyone got to a keyboard), so this is a real,
     * accepted field, not a value the server always owns. */
    disclosedAt: z.iso.datetime({ offset: true }).optional(),
  })
  .refine(
    (value) =>
      value.basisType === 'authorization'
        ? value.authorizationId !== undefined && value.noAuthorizationPurpose === undefined
        : value.authorizationId === undefined && value.noAuthorizationPurpose !== undefined,
    {
      error:
        'authorizationId is required (and noAuthorizationPurpose must be absent) when basisType ' +
        "is 'authorization'; noAuthorizationPurpose is required (and authorizationId must be " +
        "absent) when basisType is 'no_authorization_required'",
      path: ['basisType'],
    },
  );
export type RecordDisclosureRequest = z.infer<typeof recordDisclosureRequestSchema>;

export const disclosureResponseSchema = z.strictObject({
  id: z.uuid(),
  patientId: z.uuid(),
  authorizationId: z.uuid().nullable(),
  basisType: disclosureBasisTypeSchema,
  noAuthorizationPurpose: disclosureNoAuthorizationPurposeSchema.nullable(),
  recipientName: z.string(),
  recordTypesDisclosed: z.array(chartExportRecordTypeSchema),
  method: disclosureMethodSchema,
  disclosedByUserId: z.uuid(),
  disclosedAt: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DisclosureResponse = z.infer<typeof disclosureResponseSchema>;

export const recordDisclosureResponseSchema = z.strictObject({
  disclosure: disclosureResponseSchema,
});
export type RecordDisclosureResponse = z.infer<typeof recordDisclosureResponseSchema>;

/**
 * `authorization_not_active` — the referenced authorization's effective status
 * (`resolveDisclosureAuthorizationStatus`) is not `active` as of the disclosure's own `disclosedAt`.
 * Returned as a structured error, never a silent fallback to a different basis — `docs/decisions/
 * cmia-disclosure-scope.md`'s own text: "refuses to proceed ... does not silently fall back".
 * `authorization_wrong_patient` — `authorizationId` names a real row, but not one belonging to the
 * patient in the path — a distinct failure from `not_found` (which means no such id at all in this
 * org) because a caller who can see this error learned that the id exists, just not for this patient.
 * `already_revoked`/`not_active` — mirrors `careTeamRequests.ts`'s `CARE_TEAM_ERROR_CODES` shape for
 * the identical "this write only makes sense against a row currently in this state" case.
 */
export const DISCLOSURE_ERROR_CODES = [
  'invalid_request',
  'not_found',
  'authorization_not_active',
  'authorization_wrong_patient',
  'not_active',
  'document_content_mismatch',
  'document_malware_detected',
  'chart_access_denied',
  'audit_unavailable',
] as const;
export type DisclosureErrorCode = (typeof DISCLOSURE_ERROR_CODES)[number];
